import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";
import { GlobalLoadingScreen } from "@/components/common/GlobalLoadingScreen";
import { ShowsLoadingOverlay } from "@/components/shows-eventos/ShowsLoadingOverlay";

// Página crítica - carregada imediatamente
import Index from "./pages/Index";

// Admin lazy
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

// Lazy load de todas as outras páginas
const Bandas = lazy(() => import("./pages/Bandas"));
const Noticias = lazy(() => import("./pages/Noticias"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BandaDetails = lazy(() => import("./pages/BandaDetails"));
const Sobre = lazy(() => import("./pages/Sobre"));
const Contato = lazy(() => import("./pages/Contato"));
const ContrateBanda = lazy(() => import("./pages/ContrateBanda"));
const CadastreEvento = lazy(() => import("./pages/CadastreEvento"));
const AgendaEventos = lazy(() => import("./pages/AgendaEventos"));

const JogoMemoria = lazy(() => import("./pages/JogoMemoria"));
const Quiz = lazy(() => import("./pages/Quiz"));
const MusicaDaSorte = lazy(() => import("./pages/MusicaDaSorte"));
const CentralDeImagens = lazy(() => import("./pages/CentralDeImagens"));
const AdminCadastroMusicas = lazy(() => import("./pages/AdminCadastroMusicas"));

// Admin pages (lazy)
const AdminNoticias = lazy(() => import("./pages/AdminNoticias"));
const AdminNewsletter = lazy(() => import("./pages/AdminNewsletter"));
const AdminLGPD = lazy(() => import("./pages/AdminLGPD"));
const AdminBandas = lazy(() => import("./pages/AdminBandas"));
const AdminEventosPublicados = lazy(() => import("./pages/AdminEventosPublicados"));
const AdminEventDetails = lazy(() => import("./pages/AdminEventDetails"));
const AdminArtes = lazy(() => import("./pages/AdminArtes"));
const AdminComunicacaoPage = lazy(() => import("./pages/AdminComunicacao"));
const AdminComunicacaoEmailPage = lazy(() => import("./pages/AdminComunicacaoEmail"));
const AdminComunicacaoWhatsappPage = lazy(() => import("./pages/AdminComunicacaoWhatsapp"));
const AdminSugestoes = lazy(() => import("./pages/AdminSugestoes"));
const AdminFinanceiroPage = lazy(() => import("./pages/AdminFinanceiro"));

// Admin Propostas e Contratos
const PropostasContratosHome = lazy(() => import("./pages/admin-propostas/PropostasContratosHome"));
const AdminPropostasList = lazy(() => import("./pages/admin-propostas/PropostasList"));
const AdminPropostaEditor = lazy(() => import("./pages/admin-propostas/PropostaEditor"));
const AdminContratosList = lazy(() => import("./pages/admin-propostas/ContratosList"));
const AdminContratoEditor = lazy(() => import("./pages/admin-propostas/ContratoEditor"));
const AdminContratosBandaList = lazy(() => import("./pages/admin-propostas/ContratosBandaList"));
const AdminContratoBandaEditor = lazy(() => import("./pages/admin-propostas/ContratoBandaEditor"));
const AdminEventoDocumentos = lazy(() => import("./pages/admin-propostas/EventoDocumentos"));

const Auth = lazy(() => import("./pages/Auth"));
const AdminAccess = lazy(() => import("./pages/AdminAccess"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));

// News & Events
const NoticiaDetails = lazy(() => import("./pages/NoticiaDetails"));
const EventoDetails = lazy(() => import("./pages/EventoDetails"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));

// Producer pages (lazy)
const ProducerLayout = lazy(() => import("./components/producer/ProducerLayout"));
const ProducerDashboardHome = lazy(() => import("./pages/producer/ProducerDashboardHome"));
const CreateEventMobile = lazy(() => import("./pages/CreateEventMobile"));
const MeusEventos = lazy(() => import("./pages/producer/MeusEventos"));
const Participantes = lazy(() => import("./pages/producer/Participantes"));
const Ingressos = lazy(() => import("./pages/producer/Ingressos"));
const Financeiro = lazy(() => import("./pages/producer/Financeiro"));
const Colaboradores = lazy(() => import("./pages/producer/Colaboradores"));
const CRM = lazy(() => import("./pages/producer/CRM"));
const ProducerRelatorios = lazy(() => import("./pages/producer/Relatorios"));
const ProducerConfiguracoes = lazy(() => import("./pages/producer/Configuracoes"));

// Band panel pages (lazy)
const PainelLayout = lazy(() => import("./pages/painel-banda/PainelLayout"));
const Dashboard = lazy(() => import("./pages/painel-banda/Dashboard"));
const Clientes = lazy(() => import("./pages/painel-banda/Clientes"));
const ClienteForm = lazy(() => import("./pages/painel-banda/ClienteForm"));
const PainelEventos = lazy(() => import("./pages/painel-banda/Eventos"));
const EventoForm = lazy(() => import("./pages/painel-banda/EventoForm"));
const Agenda = lazy(() => import("./pages/painel-banda/Agenda"));
const PropostaEditor = lazy(() => import("./pages/painel-banda/PropostaEditor"));
const PropostaEditorV2 = lazy(() => import("./pages/painel-banda/PropostaEditorV2"));
const Contratos = lazy(() => import("./pages/painel-banda/Contratos"));
const FinanceiroPainel = lazy(() => import("./pages/painel-banda/Financeiro"));
const Receitas = lazy(() => import("./pages/painel-banda/Receitas"));
const ReceitaForm = lazy(() => import("./pages/painel-banda/ReceitaForm"));
const Despesas = lazy(() => import("./pages/painel-banda/Despesas"));
const DespesaForm = lazy(() => import("./pages/painel-banda/DespesaForm"));
const Contatos = lazy(() => import("./pages/painel-banda/Contatos"));
const ContatoForm = lazy(() => import("./pages/painel-banda/ContatoForm"));
const Musicos = lazy(() => import("./pages/painel-banda/Musicos"));
const MusicoForm = lazy(() => import("./pages/painel-banda/MusicoForm"));
const Relatorios = lazy(() => import("./pages/painel-banda/Relatorios"));
const Configuracoes = lazy(() => import("./pages/painel-banda/Configuracoes"));
const ClienteDetails = lazy(() => import("./pages/painel-banda/ClienteDetails"));
const PainelEventoDetails = lazy(() => import("./pages/painel-banda/EventoDetails"));
const Recebimento = lazy(() => import("./pages/painel-banda/Recebimento"));
const PropostaEditorPreview = lazy(() => import("./pages/painel-banda/PropostaEditorPreview"));

// Other pages
const TermosUso = lazy(() => import("./pages/TermosUso"));
const TermosPrivacidade = lazy(() => import("./pages/TermosPrivacidade"));
const RedefinirSenha = lazy(() => import("./pages/RedefinirSenha"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Descadastrar = lazy(() => import("./pages/Descadastrar"));
const Cadastre = lazy(() => import("./pages/Cadastre"));
const VerificarRecibo = lazy(() => import("./pages/VerificarRecibo"));
const AdminAssinaturas = lazy(() => import("./pages/AdminAssinaturas"));
const AdminContatoBandas = lazy(() => import("./pages/AdminContatoBandas"));
const AdminContatoClubes = lazy(() => import("./pages/AdminContatoClubes"));
const AdminContatoRadios = lazy(() => import("./pages/AdminContatoRadios"));
const CompleteSignup = lazy(() => import("./pages/CompleteSignup"));
const CadastreBanda = lazy(() => import("./pages/CadastreBanda"));
const AdminBandaBalada = lazy(() => import("./pages/AdminBandaBalada"));

const ShowsLogin = lazy(() => import("./pages/shows-eventos/ShowsLogin"));
const ShowsLayout = lazy(() => import("./components/shows-eventos/ShowsLayout"));
const ShowsEventosDashboard = lazy(() => import("./pages/shows-eventos/ShowsEventosDashboard"));
const ShowsEventosUpload = lazy(() => import("./pages/shows-eventos/ShowsEventosUpload"));
const ShowsEventosLista = lazy(() => import("./pages/shows-eventos/ShowsEventosLista"));
const ShowsEventoDetalhe = lazy(() => import("./pages/shows-eventos/ShowsEventoDetalhe"));
const ShowsBandasGerenciar = lazy(() => import("./pages/shows-eventos/ShowsBandasGerenciar"));
const ShowsContatosGerenciar = lazy(() => import("./pages/shows-eventos/ShowsContatosGerenciar"));
const ShowsRelatorios = lazy(() => import("./pages/shows-eventos/ShowsRelatorios"));
const ShowsConfiguracoes = lazy(() => import("./pages/shows-eventos/ShowsConfiguracoes"));
const ShowsBandaPerfil = lazy(() => import("./pages/shows-eventos/ShowsBandaPerfil"));
const ShowsImportacoes = lazy(() => import("./pages/shows-eventos/ShowsImportacoes"));


function AppSuspenseFallback() {
  const { pathname } = useLocation();
  return pathname.startsWith('/admin/showseeventos') ? (
    <ShowsLoadingOverlay />
  ) : (
    <GlobalLoadingScreen />
  );
}

function App() {
  return (
    <TooltipProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<AppSuspenseFallback />}>
          <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/bandas" element={<Bandas />} />
          <Route path="/banda/:id" element={<BandaDetails />} />
          <Route path="/sobre" element={<Sobre />} />
          <Route path="/contato" element={<Contato />} />
          <Route path="/contrate-banda" element={<ContrateBanda />} />
          <Route path="/cadastre-sua-banda" element={<CadastreBanda />} />
          <Route path="/cadastre-evento" element={<CadastreEvento />} />
          <Route path="/eventos" element={<Navigate to="/agenda-eventos" replace />} />
          <Route path="/agenda-eventos" element={<AgendaEventos />} />
          
          <Route path="/jogo-memoria" element={<JogoMemoria />} />
          <Route path="/quiz" element={<Quiz />} />
          <Route path="/musica-da-sorte" element={<MusicaDaSorte />} />
          
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/noticias" element={<AdminNoticias />} />
          <Route path="/admin/newsletter" element={<AdminNewsletter />} />
          <Route path="/admin/lgpd" element={<AdminLGPD />} />
          <Route path="/admin/bandas" element={<AdminBandas />} />
          <Route path="/admin/eventos-publicados" element={<AdminEventosPublicados />} />
          <Route path="/admin/evento/:id" element={<AdminEventDetails />} />
          <Route path="/admin/artes" element={<AdminArtes />} />
          <Route path="/admin/comunicacao" element={<AdminComunicacaoPage />} />
          <Route path="/admin/comunicacao/email" element={<AdminComunicacaoEmailPage />} />
          <Route path="/admin/comunicacao/whatsapp" element={<AdminComunicacaoWhatsappPage />} />
          <Route path="/admin/financeiro" element={<AdminFinanceiroPage />} />
          <Route path="/admin/sugestoes" element={<AdminSugestoes />} />
          <Route path="/admin/propostas-contratos" element={<PropostasContratosHome />} />
          <Route path="/admin/propostas" element={<AdminPropostasList />} />
          <Route path="/admin/propostas/nova" element={<AdminPropostaEditor />} />
          <Route path="/admin/propostas/editar/:id" element={<AdminPropostaEditor />} />
          <Route path="/admin/contratos-admin" element={<AdminContratosList />} />
          <Route path="/admin/contratos-admin/novo" element={<AdminContratoEditor />} />
          <Route path="/admin/contratos-admin/editar/:id" element={<AdminContratoEditor />} />
          <Route path="/admin/contratos-banda" element={<AdminContratosBandaList />} />
          <Route path="/admin/contratos-banda/novo" element={<AdminContratoBandaEditor />} />
          <Route path="/admin/contratos-banda/editar/:id" element={<AdminContratoBandaEditor />} />
          <Route path="/admin/evento-documentos/:eventoKey" element={<AdminEventoDocumentos />} />
          <Route path="/admin/assinaturas" element={<AdminAssinaturas />} />
          <Route path="/admin/contatobandas" element={<AdminContatoBandas />} />
          <Route path="/admin/contatoclubes" element={<AdminContatoClubes />} />
          <Route path="/admin/contatoradios" element={<AdminContatoRadios />} />
          <Route path="/admin/bandabalada" element={<AdminBandaBalada />} />
          <Route path="/admin/centraldeimagens" element={<CentralDeImagens />} />
          <Route path="/admin/cadastro-musicas" element={<AdminCadastroMusicas />} />

          <Route path="/noticias" element={<Noticias />} />
          <Route path="/noticia/:slug" element={<NoticiaDetails />} />
          <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
          <Route path="/evento/:slug" element={<EventoDetails />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/admin-access" element={<AdminAccess />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          
          <Route path="/painel-produtor" element={<ProducerLayout />}>
            <Route index element={<ProducerDashboardHome />} />
            <Route path="criar-evento" element={<CreateEventMobile />} />
            <Route path="eventos" element={<MeusEventos />} />
            <Route path="participantes" element={<Participantes />} />
            <Route path="ingressos" element={<Ingressos />} />
            <Route path="financeiro" element={<Financeiro />} />
            <Route path="colaboradores" element={<Colaboradores />} />
            <Route path="crm" element={<CRM />} />
            <Route path="relatorios" element={<ProducerRelatorios />} />
            <Route path="configuracoes" element={<ProducerConfiguracoes />} />
          </Route>
          
          {/* Band Panel Routes */}
          <Route path="/painel-banda" element={<PainelLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="clientes" element={<Clientes />} />
            <Route path="clientes/novo" element={<ClienteForm />} />
            <Route path="clientes/editar/:id" element={<ClienteForm />} />
            <Route path="clientes/:id" element={<ClienteDetails />} />
            <Route path="eventos" element={<PainelEventos />} />
            <Route path="eventos/novo" element={<EventoForm />} />
            <Route path="eventos/editar/:id" element={<EventoForm />} />
            <Route path="eventos/:id" element={<PainelEventoDetails />} />
            <Route path="recebimento/:eventoId" element={<Recebimento />} />
            <Route path="agenda" element={<Agenda />} />
            <Route path="propostas/editar/:id" element={<PropostaEditor />} />
            <Route path="propostas/editar-v2/:id" element={<PropostaEditorV2 />} />
            <Route path="propostas/:historicoId/editar" element={<PropostaEditorPreview />} />
            <Route path="contratos" element={<Contratos />} />
            <Route path="financeiro" element={<FinanceiroPainel />} />
            <Route path="receitas" element={<Receitas />} />
            <Route path="receitas/nova" element={<ReceitaForm />} />
            <Route path="receitas/editar/:id" element={<ReceitaForm />} />
            <Route path="despesas" element={<Despesas />} />
            <Route path="despesas/nova" element={<DespesaForm />} />
            <Route path="despesas/editar/:id" element={<DespesaForm />} />
            <Route path="contatos" element={<Contatos />} />
            <Route path="contatos/novo" element={<ContatoForm />} />
            <Route path="contatos/editar/:id" element={<ContatoForm />} />
            <Route path="musicos" element={<Musicos />} />
            <Route path="musicos/novo" element={<MusicoForm />} />
            <Route path="musicos/editar/:id" element={<MusicoForm />} />
            <Route path="relatorios" element={<Relatorios />} />
            <Route path="configuracoes" element={<Configuracoes />} />
          </Route>
          
          <Route path="/redefinir-senha" element={<RedefinirSenha />} />
          <Route path="/complete-signup" element={<CompleteSignup />} />
          <Route path="/termos-uso" element={<TermosUso />} />
          <Route path="/termos-privacidade" element={<TermosPrivacidade />} />
          
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/descadastrar" element={<Descadastrar />} />
          <Route path="/verificar-recibo" element={<VerificarRecibo />} />
          <Route path="/cadastre" element={<Cadastre />} />
          {/* Redirect old URLs */}
          <Route path="/showseeventos" element={<Navigate to="/admin/showseeventos/dashboard" replace />} />
          <Route path="/showseeventos/*" element={<Navigate to="/admin/showseeventos/dashboard" replace />} />
          
          {/* Shows e Eventos under admin */}
          <Route path="/admin/showseeventos" element={<ShowsLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<ShowsEventosDashboard />} />
            <Route path="upload" element={<ShowsEventosUpload />} />
            <Route path="importacoes" element={<ShowsImportacoes />} />
            <Route path="eventos" element={<ShowsEventosLista />} />
            <Route path="eventos/:id" element={<ShowsEventoDetalhe />} />
            <Route path="bandas" element={<ShowsBandasGerenciar />} />
            <Route path="banda/:id" element={<ShowsBandaPerfil />} />
            <Route path="contatos" element={<ShowsContatosGerenciar />} />
            <Route path="relatorios" element={<ShowsRelatorios />} />
            <Route path="configuracoes" element={<ShowsConfiguracoes />} />
          </Route>
          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <Toaster position="top-center" toastOptions={{ style: { zIndex: 9999 } }} />
      </BrowserRouter>
    </TooltipProvider>
  );
}

export default App;
